// components/Blog/TodaysBlogWidget.jsx
import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Button,
  Collapse
} from "reactstrap";
import moment from "moment";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const TodaysBlogWidget = () => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const res = await fetch(`${API_BASE}/blog?limit=1`);
        const json = await res.json();

        // Check if article is from today (or recent)
        const todaysArticle = json.articles?.[0];
        if (todaysArticle) {
          const articleDate = moment(todaysArticle.pubDate);
          const isRecent = moment().diff(articleDate, 'hours') < 48;

          if (isRecent) {
            setArticle(todaysArticle);
          }
        }
      } catch (err) {
        console.error("Failed to load blog article:", err);
        setArticle(null);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, []);

  if (loading) {
    return (
      <Card className="shadow bg-gradient-default">
        <CardBody className="text-center py-5">
          <Spinner color="light" />
        </CardBody>
      </Card>
    );
  }

  if (!article) return null; // Don't show widget if no recent article

  const publishedAgo = moment(article.pubDate).fromNow();

  return (
    <Card className="shadow bg-gradient-default text-white">
      <CardHeader className="bg-transparent border-0">
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <div className="mb-2 mb-md-0">
            <h5 className="text-white mb-1">
              <i className="fas fa-newspaper mr-2" />
              Latest from East Street Weather Blog
            </h5>
            <small className="text-white" style={{ opacity: 0.8 }}>
              by {article.author} • {publishedAgo}
            </small>
          </div>
          <a
            href="/admin/blog"
            className="btn btn-sm btn-outline-white"
          >
            View All Posts
          </a>
        </div>
      </CardHeader>
      <CardBody className="pt-0">
        <h3 className="text-white mb-3">{article.title}</h3>

        {article.imageUrl && !expanded && (
          <img
            src={article.imageUrl}
            alt={article.title}
            className="img-fluid rounded mb-3"
            style={{ maxHeight: '300px', objectFit: 'cover', width: '100%' }}
          />
        )}

        <div className="text-white" style={{ opacity: 0.95 }}>
          <Collapse isOpen={!expanded}>
            <div>
              {article.summary.substring(0, 250)}
              {article.summary.length > 250 && '...'}
              <Button
                color="link"
                className="text-white p-0 ml-2 font-weight-bold"
                onClick={() => setExpanded(true)}
                style={{ textDecoration: 'underline' }}
              >
                Read more
              </Button>
            </div>
          </Collapse>

          <Collapse isOpen={expanded}>
            <div>
              {article.imageUrl && (
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="img-fluid rounded mb-3"
                  style={{ maxHeight: '400px', objectFit: 'cover', width: '100%' }}
                />
              )}
              <div
                className="blog-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
                style={{ lineHeight: '1.6' }}
              />
              <div className="mt-3">
                <Button
                  color="link"
                  className="text-white p-0 font-weight-bold"
                  onClick={() => setExpanded(false)}
                  style={{ textDecoration: 'underline' }}
                >
                  Show less
                </Button>
                <span className="mx-2">•</span>
                <a
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white"
                  style={{ textDecoration: 'underline' }}
                >
                  View on WordPress <i className="fas fa-external-link-alt ml-1" style={{ fontSize: '0.8rem' }} />
                </a>
              </div>
            </div>
          </Collapse>
        </div>
      </CardBody>
    </Card>
  );
};

export default TodaysBlogWidget;
